<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Rapport de Stock — Ingrédients</h1>
        <p class="page-subtitle" v-if="rows.length">{{ rows.length }} ligne{{ rows.length > 1 ? 's' : '' }} — Valeur: {{ fmt(totalCost) }}</p>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <v-btn color="error" outlined small @click="exportPDF" v-if="rows.length"><v-icon left small>mdi-file-pdf-box</v-icon>PDF</v-btn>
        <v-btn color="success" outlined small @click="exportExcel" v-if="rows.length"><v-icon left small>mdi-file-excel</v-icon>Excel</v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <div class="d-flex justify-end mb-3" style="gap: 8px;">
        <v-select
          v-model="store"
          :items="stores"
          item-text="name"
          item-value="id"
          outlined dense hide-details clearable
          placeholder="Tous les emplacements"
          prepend-inner-icon="mdi-store-outline"
          style="max-width: 240px;"
          @change="load"
        ></v-select>
        <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
      </div>

      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">INGRÉDIENT</th>
                <th class="text-left">UNITÉ</th>
                <th class="text-right">INITIAL</th>
                <th class="text-right">RÉCEPTIONS</th>
                <th class="text-right">AJUST.</th>
                <th class="text-right">SORTIES</th>
                <th class="text-right">FINAL</th>
                <th class="text-right">VALEUR COÛT</th>
              </tr>
              <tr class="totals-row">
                <th class="text-left" colspan="2">TOTAL ({{ filteredRows.length }})</th>
                <th class="text-right">{{ fmt(sumField('initial')) }}</th>
                <th class="text-right">{{ fmt(sumField('receivings')) }}</th>
                <th class="text-right">{{ fmt(sumField('adjustments')) }}</th>
                <th class="text-right">{{ fmt(sumField('sales')) }}</th>
                <th class="text-right">{{ fmt(totalFinal) }}</th>
                <th class="text-right">{{ fmt(totalCost) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in filteredRows" :key="idx">
                <td class="font-weight-medium">{{ item.ingredient_name }}</td>
                <td>{{ item.unit }}</td>
                <td class="text-right">{{ fmt(item.initial) }}</td>
                <td class="text-right">{{ fmt(item.receivings) }}</td>
                <td class="text-right">{{ fmt(item.adjustments) }}</td>
                <td class="text-right">{{ fmt(item.sales) }}</td>
                <td class="text-right font-weight-bold" :class="parseFloat(item.final) < 0 ? 'error--text' : ''">{{ fmt(item.final) }}</td>
                <td class="text-right">{{ fmt(parseFloat(item.final) * parseFloat(item.cost || 0)) }}</td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td colspan="8" class="text-center text--secondary pa-6">Aucune donnée</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'IngredientStockView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Stock Ingrédients', disabled: true, href: '/ingredient-stock' }],
    enable: false,
    search: '',
    rows: [],
    stores: [],
    store: null,
  }),
  computed: {
    filteredRows() {
      if (!this.search.trim()) return this.rows
      var q = this.search.toLowerCase()
      return this.rows.filter(function (r) { return r.ingredient_name && r.ingredient_name.toLowerCase().indexOf(q) !== -1 })
    },
    totalFinal() { return this.filteredRows.reduce(function (s, r) { return s + parseFloat(r.final || 0) }, 0) },
    totalCost() { return this.filteredRows.reduce(function (s, r) { return s + parseFloat(r.final || 0) * parseFloat(r.cost || 0) }, 0) },
  },
  methods: {
    fmt(v) { var n = parseFloat(v); if (!n && n !== 0) return '0'; return n.toFixed(2) },
    sumField(field) { return this.filteredRows.reduce(function (s, r) { return s + parseFloat(r[field] || 0) }, 0) },
    load() {
      var self = this
      var isValid = function (v) { return v && v !== 'null' && v.trim() !== '' }
      var from = isValid(localStorage.debut_periode) ? localStorage.debut_periode : ''
      var to = isValid(localStorage.fin_periode) ? localStorage.fin_periode : ''
      var url = '/reports/ingredient_stock?from_date=' + from + '&to_date=' + to
      if (this.store) url += '&store_id=' + this.store
      this.$axios.get(url)
        .then(function (r) { self.rows = (r.data && r.data.result) || []; self.enable = true })
        .catch(function () { self.enable = true })
    },
    loadStores() {
      var self = this
      try {
        var biz = JSON.parse(localStorage.business)
        this.$axios.get('/stores?orderasc=name&business_id=' + biz.id)
          .then(function (r) { self.stores = r.data || [] })
      } catch (e) { /* ignore */ }
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], border = [220, 225, 230], light = [248, 250, 252]

      doc.setFillColor(...primary); doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('RAPPORT DE STOCK — INGRÉDIENTS', ml, 11)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      doc.text((localStorage.debut_periode || '') + ' — ' + (localStorage.fin_periode || ''), mr, 8, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 13, { align: 'right' })

      var y = 24
      var cols = [
        { label: 'INGRÉDIENT', w: 50, align: 'left' },
        { label: 'UNITÉ', w: 18, align: 'left' },
        { label: 'INITIAL', w: 22, align: 'right' },
        { label: 'RÉCEPT.', w: 22, align: 'right' },
        { label: 'AJUST.', w: 22, align: 'right' },
        { label: 'SORTIES', w: 22, align: 'right' },
        { label: 'FINAL', w: 22, align: 'right' },
        { label: 'VAL. COÛT', w: 28, align: 'right' },
      ]
      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW; cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0); cols[0].w += (cw - actualW)
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 6.5

      function drawHeader() {
        doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : c.x + 3; doc.text(c.label, tx, y + 4.5, { align: c.align }) })
        y += rowH + 1
      }
      drawHeader()

      self.filteredRows.forEach(function (r, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var fin = parseFloat(r.final || 0)
        var name = r.ingredient_name || ''; if (name.length > 26) name = name.substring(0, 24) + '..'
        var vals = [name, r.unit || '', self.fmt(r.initial), self.fmt(r.receivings), self.fmt(r.adjustments), self.fmt(r.sales), self.fmt(fin), self.fmt(fin * parseFloat(r.cost || 0))]
        cols.forEach(function (c, i) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : c.x + 3
          if (i === 6 && fin < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(...dark)
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      // Totals
      y += 1; doc.setFillColor(...light); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(...primary); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...dark)
      doc.text('TOTAL', ml + 3, y + 4.8)
      doc.text(self.fmt(self.sumField('initial')), cols[2].x + cols[2].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.sumField('receivings')), cols[3].x + cols[3].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.sumField('adjustments')), cols[4].x + cols[4].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.sumField('sales')), cols[5].x + cols[5].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.totalFinal), cols[6].x + cols[6].w - 3, y + 4.8, { align: 'right' })
      doc.text(self.fmt(self.totalCost), cols[7].x + cols[7].w - 3, y + 4.8, { align: 'right' })

      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p); doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('rapport_stock_ingredients.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var wsData = [['RAPPORT DE STOCK — INGRÉDIENTS'], [biz.name || ''], ['Du ' + (localStorage.debut_periode || '') + ' au ' + (localStorage.fin_periode || '')], []]
      wsData.push(['Ingrédient', 'Unité', 'Initial', 'Réceptions', 'Ajustements', 'Sorties', 'Stock Final', 'Valeur Coût'])
      self.filteredRows.forEach(function (r) {
        var fin = parseFloat(r.final || 0)
        wsData.push([r.ingredient_name, r.unit, parseFloat(r.initial || 0), parseFloat(r.receivings || 0), parseFloat(r.adjustments || 0), parseFloat(r.sales || 0), fin, Math.round(fin * parseFloat(r.cost || 0) * 100) / 100])
      })
      wsData.push([])
      wsData.push(['TOTAL', '', self.sumField('initial'), self.sumField('receivings'), self.sumField('adjustments'), self.sumField('sales'), Math.round(self.totalFinal * 100) / 100, Math.round(self.totalCost * 100) / 100])
      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 28 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 16 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: 4, c: 7 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } }]
      for (var r = 5; r < wsData.length; r++) { for (var c = 7; c <= 7; c++) { var cell = ws[XLSX.utils.encode_cell({ r: r, c: c })]; if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = '#,##0.00' } } }
      XLSX.utils.book_append_sheet(wb, ws, 'Stock'); XLSX.writeFile(wb, 'rapport_stock_ingredients.xlsx')
    },
  },
  mounted() { this.load(); this.loadStores() },
}
</script>

<style scoped>
.search-input { width: 260px; padding: 8px 12px; font-size: 0.8125rem; border: 1px solid #cbd5e1; border-radius: 6px; outline: none; }
.search-input:focus { border-color: #194173; box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1); }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; overflow: hidden; }
.table-responsive { overflow-x: auto; max-height: 600px; overflow-y: auto; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th { position: sticky; top: 0; background: #f8fafc; font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 12px; border-bottom: 2px solid #e8ecf1; white-space: nowrap; z-index: 1; }
.report-table .totals-row th { background: #f1f5f9; color: #1a2332; font-weight: 700; font-size: 0.75rem; border-bottom: 2px solid #cbd5e1; }
.report-table tbody td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }
</style>
